import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, X, Send, Info, Check, Search } from 'lucide-react';
import { LensData } from '../types';
import { CAREER_LEVELS, RECENCY_TRIGGERS, US_STATES } from '../constants';
type FormType = 'net_worth' | 'assets' | 'career' | 'recency' | 'states' | 'summary' | null;
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string | React.ReactNode;
  formType?: FormType;
}
export function CopilotChat({
  initialPrompt,
  onClose,
  onGenerateLens
}: {
  initialPrompt: string;
  onClose: () => void;
  onGenerateLens?: (data: LensData) => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [chatInput, setChatInput] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<LensData>({
    netWorth: {
      min: '',
      max: ''
    },
    assets: {
      min: '',
      max: ''
    },
    careers: [],
    recency: [],
    states: []
  });
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking]);
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'init-user',
        role: 'user',
        content: initialPrompt
      }]);
      setIsThinking(true);
      setTimeout(() => {
        setIsThinking(false);
        setMessages(prev => [...prev, {
          id: 'ai-intro',
          role: 'ai',
          content: "I'll help you create a Market Lens! Let me ask a few questions to define your target audience."
        }, {
          id: 'ai-step1',
          role: 'ai',
          content: 'What net worth range are you targeting?',
          formType: 'net_worth'
        }]);
        setCurrentStep(1);
      }, 1500);
    }
  }, [initialPrompt, messages.length]);
  const handleNextStep = (userSummary: string, nextAiMessage: string, nextFormType: FormType, nextStepNum: number) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'user',
      content: userSummary
    }]);
    setIsThinking(true);
    setTimeout(() => {
      setIsThinking(false);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: nextAiMessage,
        formType: nextFormType
      }]);
      setCurrentStep(nextStepNum);
    }, 1500);
  };
  const renderForm = (msg: Message) => {
    const isCompleted = currentStep > getStepNumber(msg.formType);
    if (msg.formType === 'net_worth') {
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm w-full max-w-md" data-id="element-15">
          <div className="flex space-x-4 mb-4" data-id="element-16">
            <div className="flex-1" data-id="element-17">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-18">
                Minimum
              </label>
              <input type="text" disabled={isCompleted} value={formData.netWorth.min} onChange={e => setFormData({
              ...formData,
              netWorth: {
                ...formData.netWorth,
                min: e.target.value
              }
            })} placeholder="$0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 disabled:bg-gray-50" data-id="element-19" />
            </div>
            <div className="flex-1" data-id="element-20">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-21">
                Maximum
              </label>
              <input type="text" disabled={isCompleted} value={formData.netWorth.max} onChange={e => setFormData({
              ...formData,
              netWorth: {
                ...formData.netWorth,
                max: e.target.value
              }
            })} placeholder="$ Any" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 disabled:bg-gray-50" data-id="element-22" />
            </div>
          </div>
          {!isCompleted && <button onClick={() => handleNextStep(`Net Worth: ${formData.netWorth.min || '$0'} - ${formData.netWorth.max || 'Any'}`, 'What range of investable assets should they have?', 'assets', 2)} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 rounded-lg transition-colors text-sm" data-id="element-23">
              Continue
            </button>}
        </div>;
    }
    if (msg.formType === 'assets') {
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm w-full max-w-md" data-id="element-24">
          <div className="flex space-x-4 mb-4" data-id="element-25">
            <div className="flex-1" data-id="element-26">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-27">
                Minimum
              </label>
              <input type="text" disabled={isCompleted} value={formData.assets.min} onChange={e => setFormData({
              ...formData,
              assets: {
                ...formData.assets,
                min: e.target.value
              }
            })} placeholder="$0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 disabled:bg-gray-50" data-id="element-28" />
            </div>
            <div className="flex-1" data-id="element-29">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-30">
                Maximum
              </label>
              <input type="text" disabled={isCompleted} value={formData.assets.max} onChange={e => setFormData({
              ...formData,
              assets: {
                ...formData.assets,
                max: e.target.value
              }
            })} placeholder="$ Any" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 disabled:bg-gray-50" data-id="element-31" />
            </div>
          </div>
          {!isCompleted && <button onClick={() => handleNextStep(`Investable Assets: ${formData.assets.min || '$0'} - ${formData.assets.max || 'Any'}`, 'What career levels are you interested in?', 'career', 3)} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 rounded-lg transition-colors text-sm" data-id="element-32">
              Continue
            </button>}
        </div>;
    }
    if (msg.formType === 'career') {
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm w-full max-w-md" data-id="element-33">
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-34">
            {CAREER_LEVELS.map(level => {
            const isSelected = formData.careers.includes(level);
            return <button key={level} disabled={isCompleted} onClick={() => setFormData(prev => ({
              ...prev,
              careers: isSelected ? prev.careers.filter(c => c !== level) : [...prev.careers, level]
            }))} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isSelected ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-300 text-gray-600 hover:border-teal-400'} ${isCompleted && !isSelected ? 'opacity-50' : ''}`} data-id="element-35">
                  {level}
                </button>;
          })}
          </div>
          {!isCompleted && <button onClick={() => handleNextStep(`Career Levels: ${formData.careers.length > 0 ? formData.careers.join(', ') : 'Any'}`, 'Any recent life events to filter by?', 'recency', 4)} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 rounded-lg transition-colors text-sm" data-id="element-36">
              Continue
            </button>}
        </div>;
    }
    if (msg.formType === 'recency') {
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm w-full max-w-md" data-id="element-37">
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-38">
            {RECENCY_TRIGGERS.map(trigger => {
            const isSelected = formData.recency.includes(trigger);
            return <button key={trigger} disabled={isCompleted} onClick={() => setFormData(prev => ({
              ...prev,
              recency: isSelected ? prev.recency.filter(t => t !== trigger) : [...prev.recency, trigger]
            }))} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isSelected ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-300 text-gray-600 hover:border-teal-400'} ${isCompleted && !isSelected ? 'opacity-50' : ''}`} data-id="element-39">
                  {trigger}
                </button>;
          })}
          </div>
          {!isCompleted && <button onClick={() => handleNextStep(`Recency Triggers: ${formData.recency.length > 0 ? formData.recency.join(', ') : 'None'}`, 'Which U.S. states should we focus on?', 'states', 5)} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 rounded-lg transition-colors text-sm" data-id="element-40">
              Continue
            </button>}
        </div>;
    }
    if (msg.formType === 'states') {
      const filteredStates = US_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm w-full max-w-md" data-id="element-41">
          <div className="mb-4" data-id="element-42">
            {!isCompleted && <div className="relative mb-3" data-id="element-43">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-44" />
                <input type="text" value={stateSearch} onChange={e => setStateSearch(e.target.value)} placeholder="Search states..." className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-teal-500" data-id="element-45" />
              </div>}
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1" data-id="element-46">
              {filteredStates.map(state => {
              const isSelected = formData.states.includes(state);
              return <button key={state} disabled={isCompleted} onClick={() => setFormData(prev => ({
                ...prev,
                states: isSelected ? prev.states.filter(s => s !== state) : [...prev.states, state]
              }))} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${isSelected ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-300 text-gray-600 hover:border-teal-400'} ${isCompleted && !isSelected ? 'hidden' : ''}`} data-id="element-47">
                    {state}{' '}
                    {isSelected && !isCompleted && <X size={12} className="inline ml-1" data-id="element-48" />}
                  </button>;
            })}
            </div>
          </div>
          {!isCompleted && <button onClick={() => handleNextStep(`States: ${formData.states.length > 0 ? formData.states.join(', ') : 'All'}`, "Here's your Market Lens configuration:", 'summary', 6)} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 rounded-lg transition-colors text-sm" data-id="element-49">
              Continue
            </button>}
        </div>;
    }
    if (msg.formType === 'summary') {
      return <div className="mt-4 bg-white border border-gray-200 rounded-xl p-5 shadow-sm w-full max-w-md" data-id="element-50">
          <h4 className="font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-51">
            Market Lens Summary
          </h4>
          <div className="space-y-3 text-sm" data-id="element-52">
            <div className="flex justify-between" data-id="element-53">
              <span className="text-gray-500" data-id="element-54">Net Worth</span>
              <span className="font-medium text-gray-900" data-id="element-55">
                {formData.netWorth.min || '$0'} -{' '}
                {formData.netWorth.max || 'Any'}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-56">
              <span className="text-gray-500" data-id="element-57">Investable Assets</span>
              <span className="font-medium text-gray-900" data-id="element-58">
                {formData.assets.min || '$0'} - {formData.assets.max || 'Any'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-59">
              <span className="text-gray-500 mb-1" data-id="element-60">Career Levels</span>
              <span className="font-medium text-gray-900" data-id="element-61">
                {formData.careers.length > 0 ? formData.careers.join(', ') : 'Any'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-62">
              <span className="text-gray-500 mb-1" data-id="element-63">Recency Triggers</span>
              <span className="font-medium text-gray-900" data-id="element-64">
                {formData.recency.length > 0 ? formData.recency.join(', ') : 'None'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-65">
              <span className="text-gray-500 mb-1" data-id="element-66">States</span>
              <span className="font-medium text-gray-900" data-id="element-67">
                {formData.states.length > 0 ? formData.states.join(', ') : 'All'}
              </span>
            </div>
          </div>
          <button onClick={() => onGenerateLens?.(formData)} className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center" data-id="element-68">
            <Check size={18} className="mr-2" data-id="element-69" /> Generate Market Lens
          </button>
        </div>;
    }
    return null;
  };
  const getStepNumber = (type: FormType) => {
    switch (type) {
      case 'net_worth':
        return 1;
      case 'assets':
        return 2;
      case 'career':
        return 3;
      case 'recency':
        return 4;
      case 'states':
        return 5;
      case 'summary':
        return 6;
      default:
        return 0;
    }
  };
  return <div className="flex flex-col bg-white rounded-2xl shadow-sm border border-gray-200 h-full overflow-hidden relative" data-id="element-70">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white z-10 shrink-0" data-id="element-71">
        <div className="flex items-center space-x-3" data-id="element-72">
          <div className="w-6 h-6 bg-teal-500 rounded flex items-center justify-center text-white font-bold text-sm" data-id="element-73">
            W
          </div>
          <h2 className="font-semibold text-gray-900" data-id="element-74">Windfall AI Copilot</h2>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-75">
            Beta
          </span>
        </div>
        <div className="flex items-center space-x-2 text-gray-400" data-id="element-76">
          <button className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" data-id="element-77">
            <Maximize2 size={16} data-id="element-78" />
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" data-id="element-79">
            <X size={18} data-id="element-80" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white" data-id="element-81">
        <AnimatePresence initial={false} data-id="element-82">
          {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-83">
              <div className={`px-4 py-3 max-w-[85%] md:max-w-[75%] ${msg.role === 'user' ? 'bg-[#0D9488] text-white rounded-2xl rounded-tr-sm' : 'bg-[#F8FAFC] text-gray-800 rounded-2xl rounded-tl-sm border border-gray-100'}`} data-id="element-84">
                <p className="text-[15px] leading-relaxed" data-id="element-85">{msg.content}</p>
              </div>
              {msg.formType && renderForm(msg)}
            </motion.div>)}

          {isThinking && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="flex items-start" data-id="element-86">
              <div className="bg-[#E0F2F1] text-teal-600 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center space-x-1" data-id="element-87">
                <span className="text-sm font-medium mr-1" data-id="element-88">Thinking</span>
                <motion.div className="w-1 h-1 bg-teal-500 rounded-full" animate={{
              opacity: [0.3, 1, 0.3]
            }} transition={{
              repeat: Infinity,
              duration: 1.4,
              delay: 0
            }} data-id="element-89" />
                <motion.div className="w-1 h-1 bg-teal-500 rounded-full" animate={{
              opacity: [0.3, 1, 0.3]
            }} transition={{
              repeat: Infinity,
              duration: 1.4,
              delay: 0.2
            }} data-id="element-90" />
                <motion.div className="w-1 h-1 bg-teal-500 rounded-full" animate={{
              opacity: [0.3, 1, 0.3]
            }} transition={{
              repeat: Infinity,
              duration: 1.4,
              delay: 0.4
            }} data-id="element-91" />
              </div>
            </motion.div>}
        </AnimatePresence>
        <div ref={messagesEndRef} className="h-4" data-id="element-92" />
      </div>

      <div className="p-4 bg-white border-t border-gray-100 shrink-0" data-id="element-93">
        <div className="max-w-4xl mx-auto" data-id="element-94">
          <div className="relative flex items-center" data-id="element-95">
            <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Ask anything about your data" className="w-full border border-teal-500 rounded-lg pl-4 pr-12 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" onKeyDown={e => {
            if (e.key === 'Enter' && chatInput.trim()) {
              setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'user',
                content: chatInput
              }]);
              setChatInput('');
              setIsThinking(true);
              setTimeout(() => setIsThinking(false), 1500);
            }
          }} data-id="element-96" />
            <button className="absolute right-2 p-2 text-gray-400 hover:text-teal-600 transition-colors" onClick={() => {
            if (chatInput.trim()) {
              setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'user',
                content: chatInput
              }]);
              setChatInput('');
              setIsThinking(true);
              setTimeout(() => setIsThinking(false), 1500);
            }
          }} data-id="element-97">
              <Send size={18} className="transform rotate-45" data-id="element-98" />
            </button>
          </div>
          <div className="flex items-center justify-center mt-3 text-xs text-gray-500" data-id="element-99">
            <Info size={12} className="mr-1.5" data-id="element-100" />
            Copilot uses AI. Please double-check responses.
          </div>
        </div>
      </div>
    </div>;
}